import { supabase, downloadStorageDocument } from '../services/supabaseService';
import type { ProposalRow } from '../data/clientEngagementMock';

function apiBaseUrl(): string {
    const host = typeof window !== 'undefined' ? window.location.hostname : '';
    return host === 'localhost' || host === '127.0.0.1' ? 'http://localhost:4000' : '';
}

function safeFileBase(name: string): string {
    return (name || 'Kandidaat_CV').replace(/[\\/:*?"<>|]/g, '').replace(/\s+/g, ' ').trim() || 'Kandidaat_CV';
}

function triggerBlobDownload(blob: Blob, fileName: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 2000);
}

async function downloadFromUrl(url: string, fileName: string): Promise<void> {
    const res = await fetch(url);
    if (!res.ok) throw new Error('Bestand kon niet worden opgehaald.');
    const blob = await res.blob();
    triggerBlobDownload(blob, fileName);
}

function storagePathFromFormatted(formatted: Record<string, unknown>): string | null {
    const paths = [formatted.canonicalStoragePath, formatted.storage_path, formatted.storagePath];
    for (const p of paths) {
        const t = p != null ? String(p).trim() : '';
        if (t) return t;
    }
    return null;
}

export function canDownloadPortalClientCv(proposal: Pick<ProposalRow, 'hasFormattedClientCv' | 'portalCvFormatted' | 'cvPlainText'>): boolean {
    const f = proposal.portalCvFormatted;
    if (f && typeof f === 'object') {
        if (storagePathFromFormatted(f)) return true;
        if (f.pdfPublicUrl != null && String(f.pdfPublicUrl).trim()) return true;
        if (f.templateId != null && String(f.templateId).trim()) return true;
    }
    if (proposal.hasFormattedClientCv) return true;
    if (proposal.cvPlainText && String(proposal.cvPlainText).trim()) return true;
    return false;
}

export async function downloadPortalClientCv(proposal: ProposalRow): Promise<void> {
    const formatted =
        proposal.portalCvFormatted && typeof proposal.portalCvFormatted === 'object'
            ? proposal.portalCvFormatted
            : null;
    const baseName = safeFileBase(
        formatted?.documentName != null ? String(formatted.documentName) : `${proposal.name} CV`,
    );

    const storagePath = formatted ? storagePathFromFormatted(formatted) : null;
    if (storagePath) {
        const ext = storagePath.toLowerCase().includes('.pdf')
            ? '.pdf'
            : storagePath.toLowerCase().includes('.docx')
              ? '.docx'
              : '';
        await downloadStorageDocument(storagePath, `${baseName}${ext || '.docx'}`);
        return;
    }

    if (formatted?.pdfPublicUrl != null) {
        const url = String(formatted.pdfPublicUrl).trim();
        if (url) {
            await downloadFromUrl(url, `${baseName}.pdf`);
            return;
        }
    }

    const templateId = formatted?.templateId != null ? String(formatted.templateId).trim() : '';
    const recruiterUserId = String(proposal.recruiterId || '').trim();
    if (templateId && recruiterUserId) {
        const rd = formatted?.resumeData;
        const liveResumeData = rd && typeof rd === 'object' && !Array.isArray(rd) ? rd : null;
        const candidateId =
            (formatted?.candidateId != null ? String(formatted.candidateId).trim() : '') || proposal.id;

        const res = await fetch(`${apiBaseUrl()}/api/documents/generate-cv`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                candidateId: candidateId || recruiterUserId,
                templateId,
                userId: recruiterUserId,
                title: baseName,
                pages: [],
                liveResumeData,
            }),
        });
        const docData = await res.json().catch(() => ({}));
        if (!res.ok) {
            throw new Error(docData?.error || res.statusText || 'CV kon niet worden gegenereerd.');
        }
        const pdfUrl = docData?.pdfPublicUrl as string | undefined;
        if (!pdfUrl) throw new Error(docData?.pdfError || 'Geen PDF beschikbaar voor dit voorstel.');
        await downloadFromUrl(pdfUrl, `${baseName}.pdf`);
        return;
    }

    const plain = String(proposal.cvPlainText || '').trim();
    if (plain) {
        triggerBlobDownload(new Blob([plain], { type: 'text/plain;charset=utf-8' }), `${baseName}.txt`);
        return;
    }

    const { data: docs, error } = await supabase
        .from('documents')
        .select('storage_path, title, file_name, created_at')
        .eq('candidate_id', proposal.id)
        .not('storage_path', 'is', null)
        .order('created_at', { ascending: false })
        .limit(1);

    if (!error && docs?.[0]?.storage_path) {
        const d = docs[0];
        const title = String(d.title || d.file_name || baseName);
        await downloadStorageDocument(String(d.storage_path), title);
        return;
    }

    throw new Error('Geen downloadbaar CV gevonden voor deze kandidaat.');
}
